import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, Users, Baby } from "lucide-react";
import { StickerCard } from "./StickerCard";
import { StatusBadge } from "./StatusBadge";
import { PillButton } from "./PillButton";
import { useApp } from "../../context/AppContext";
import { motion } from "framer-motion";

export function ActivityCard({ activity, index = 0 }) {
  const navigate = useNavigate();
  const { activityStatus, slotRemaining } = useApp();
  const status = activityStatus(activity);

  let remaining = 0;
  if (activity.bookingRequired) {
    remaining = activity.slots.reduce((s, sl) => s + slotRemaining(activity.id, sl.id), 0);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.08 }}
    >
      <StickerCard className="h-full flex flex-col group" data-testid={`activity-card-${activity.slug}`}>
        <div className="relative h-40 sm:h-44 overflow-hidden">
          <img
            src={activity.image}
            alt={activity.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3">
            <StatusBadge status={status} />
          </div>
          <div className="absolute -bottom-6 right-4 w-16 h-16 rounded-2xl bg-white shadow-playful p-2 flex items-center justify-center">
            <img src={activity.icon} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
        <div className="p-5 pt-7 flex flex-col flex-1">
          <h3 className="font-display text-xl text-sea-deep">{activity.name}</h3>
          <p className="mt-1 text-sm text-sea-deep/70 font-semibold line-clamp-2">{activity.short}</p>

          <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-bold text-sea-deep/70">
            <span className="flex items-center gap-1"><Baby className="w-3.5 h-3.5 text-coral" /> {activity.ageRecommendation}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-coral" /> {activity.duration}</span>
            <span className="flex items-center gap-1 col-span-2"><MapPin className="w-3.5 h-3.5 text-coral" /> {activity.location}</span>
          </div>

          <div className="mt-3 min-h-[1.25rem]">
            {activity.bookingRequired ? (
              <span className="flex items-center gap-1 text-sm font-extrabold text-grass">
                <Users className="w-4 h-4" />
                {status === "fully-booked" ? "Fully Booked" : `${remaining} slot tersisa`}
              </span>
            ) : (
              <span className="text-sm font-extrabold text-sky-dark">Tanpa reservasi</span>
            )}
          </div>

          <div className="mt-4 pt-2 flex-1 flex items-end">
            <PillButton
              size="sm"
              variant={activity.bookingRequired ? "primary" : "sky"}
              className="w-full"
              onClick={() => navigate(`/activities/${activity.slug}`)}
              data-testid={`activity-view-${activity.slug}`}
            >
              {activity.bookingRequired ? "Lihat & Booking" : "Lihat Detail"}
            </PillButton>
          </div>
        </div>
      </StickerCard>
    </motion.div>
  );
}
